import React, { useEffect, useRef, useCallback } from 'react';
import { createMap } from '../services/mapService';
import ScaleBar from './ScaleBar';
import CopyrightNotice from './CopyrightNotice';
import KntrMapControls from './KntrMapControls';
import config from '../config/config';

const PITCH_0 = config.map.defaultPitch;
const BEARING_0 = config.map.defaultBearing;
const MODE_NORMAL = config.map.defaultMapMode;
/**
 * 国盗り地図コンポーネント
 */
const KntrMap = ({
  style,
  center,
  zoom,
  onMapInitialized,
  onMoveEnd,
  isCenterIndicatorEnabled,
  currentLocationButtonState,
  setCurrentLocationButtonState,
  isFromKntr
}) => {

  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  // ========================================
  // イベントハンドラ（地図操作関連）
  // ========================================

  // ズームイン処理のハンドラを定義
  const handleZoomIn = useCallback(() => {
    if (mapInstance.current) {
      mapInstance.current.zoomIn();
    }
  }, []);

  // ズームアウト処理のハンドラを定義
  const handleZoomOut = useCallback(() => {
    if (mapInstance.current) {
      mapInstance.current.zoomOut();
    }
  }, []);


  // ========================================
  // useEffect - 地図初期化（初回のみ）
  // ========================================

  // 地図の初期化
  useEffect(() => {

    // cleanupで参照するので、tryの外で宣言しておく
    let clampRotation = null;
    let handleMoveEnd = null;

    try {
      if (!mapRef.current) {
        return;
      }

      const resolvedCenter =
        center && Number.isFinite(center.lat) && Number.isFinite(center.lng)
          ? [center.lng, center.lat]
          : undefined;

      let map = null;
      const result = createMap(mapRef.current, {
        style,
        center: resolvedCenter,
        zoom,
        pitch: PITCH_0,
        bearing: BEARING_0,
        mode: MODE_NORMAL,
      });
      map = result?.map;

      if (!map) {
        return;
      }

      mapInstance.current = map;

      // 回転・傾きは無効化（ピンチズームは維持）
      try {
        map.dragRotate?.disable?.();
        map.touchZoomRotate?.disableRotation?.();
        map.touchPitch?.disable?.();
        // キーボード経由の回転/傾きも止める（Shift+矢印など）
        map.keyboard?.disable?.();

        map.setPitch?.(PITCH_0);
        map.setBearing?.(BEARING_0);
        map.setMaxPitch?.(PITCH_0);
        map.setMinPitch?.(PITCH_0);
      } catch (e) {
      }

      // bearing/pitch が変化したら即0に戻す（ブラウザ操作を完全阻止）
      clampRotation = () => {
        try {
          if (map.getBearing?.() !== BEARING_0) map.setBearing?.(BEARING_0);
          if (map.getPitch?.() !== PITCH_0) map.setPitch?.(PITCH_0);
        } catch (e) {
        }
      };

      map.on('rotate', clampRotation);
      map.on('pitch', clampRotation);

      if (onMapInitialized) {
        onMapInitialized(map);
      }

      // 地図の状態をパラメータとして渡す
      if (onMoveEnd) {
        // cleanupでoffできるようにする
        handleMoveEnd = (event) => {
          const c = map.getCenter();
          const z = map.getZoom();
          const p = map.getPitch();
          const b = map.getBearing();
          const isUserTriggered = !!event?.originalEvent;

          onMoveEnd({
            center: { lat: c.lat, lng: c.lng },
            zoom: z,
            pitch: p,
            bearing: b,
            isUserTriggered,
          });
        };
        map.on('moveend', handleMoveEnd);
      }
    } catch (error) {
    }

    // クリーンアップ関数
    return () => {
      if (mapInstance.current) {
        try {
          // イベント解除
          if (clampRotation) {
            mapInstance.current.off?.('rotate', clampRotation);
            mapInstance.current.off?.('pitch', clampRotation);
          }
          // moveendのイベントも解除
          if (handleMoveEnd) {
            mapInstance.current.off?.('moveend', handleMoveEnd);
          }
        } catch (e) {
        }
        // 地図インスタンス破棄。これをしないと、ページ遷移や再マウントで重くなったり真っ白になったりしがち
        mapInstance.current.remove();
        // 参照を切る
        mapInstance.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 初回のみ実行するため依存配列を空にしている（props の変更で再初期化しない）

  // ========================================
  // JSX Return
  // ========================================

  return (
    <>
      {/* 地図表示エリア */}
      <div id="kntr-map" ref={mapRef}>
        {/* 地図本体（MapLibreが描画する） */}
      </div>
      {/* 中心点表示 */}
      {isCenterIndicatorEnabled && (
        <div id="kntr-map-center-indicator" />
      )}
      {/* スケールバー */}
      <ScaleBar
        map={mapInstance.current}
        isFromKntr={isFromKntr}
      />
      {/* 著作権表示 */}
      <CopyrightNotice
        isFromKntr={isFromKntr}
      />
      {/* 国盗り用の右下ボタン群 */}
      <KntrMapControls
        map={mapInstance.current}
        mapRef={mapRef}
        onZoomInClick={handleZoomIn}
        onZoomOutClick={handleZoomOut}
        // 現在地ボタンの状態と更新関数
        currentLocationButtonState={currentLocationButtonState}
        setCurrentLocationButtonState={setCurrentLocationButtonState}
      />
    </>
  );
};

export default KntrMap;